import { useMemo, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, Plus, Trash, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NULL_VALUE } from '@/constants/displayConstants';
import { COMS_TYPES, INCIDENT } from '../../../../ICSConstants';
import {
  useCreateIncidentComs,
  useIncidentComs,
  useUpdateIncidentComs,
} from 'components/IAP/hooks/incidentComsHooks';
import type { IncidentComs } from 'components/IAP/hooks/incidentComsHooks';
import { selectGroupGuid, useAppSelector } from '@/slices/commonSelectors';
import { FocusHighlightCard } from './FocusHighlightCard';
import type { SectionProps } from './types';

const communicationSchema = z.object({
  type: z.enum(['Radio', 'Nonradio']),
  function: z.string().trim().min(1, 'Function / Name is required.'),
  channelName: z.string().trim().min(1, 'Channel name is required.'),
  channelNumber: z.string().optional(),
  rxFrequency: z.string().optional(),
  rxToneNac: z.string().optional(),
  txFrequency: z.string().optional(),
  txToneNac: z.string().optional(),
  mode: z.string().optional(),
  remarks: z.string().optional(),
});

type CommunicationFormValues = z.infer<typeof communicationSchema>;

const emptyCommunicationValues: CommunicationFormValues = {
  type: 'Radio',
  function: '',
  channelName: '',
  channelNumber: '',
  rxFrequency: '',
  rxToneNac: '',
  txFrequency: '',
  txToneNac: '',
  mode: '',
  remarks: '',
};

type ColumnDef = {
  key: keyof CommunicationFormValues;
  label: string;
  radioOnly?: boolean;
};

const COM_COLUMNS: ColumnDef[] = [
  { key: 'function', label: 'Function / Name *' },
  { key: 'channelName', label: 'Channel Name *' },
  { key: 'channelNumber', label: 'Channel #' },
  { key: 'rxFrequency', label: 'RX Freq', radioOnly: true },
  { key: 'rxToneNac', label: 'RX Tone/NAC', radioOnly: true },
  { key: 'txFrequency', label: 'TX Freq', radioOnly: true },
  { key: 'txToneNac', label: 'TX Tone/NAC', radioOnly: true },
  { key: 'mode', label: 'Mode', radioOnly: true },
  { key: 'remarks', label: 'Remarks' },
];

const RADIO_FIELDS: (keyof CommunicationFormValues)[] = [
  'rxFrequency',
  'rxToneNac',
  'txFrequency',
  'txToneNac',
  'mode',
];

const toCommunicationFormValues = (
  communication: IncidentComs
): CommunicationFormValues => ({
  type: communication.type?.toLowerCase() === 'nonradio' ? 'Nonradio' : 'Radio',
  function: communication.function ?? communication.name ?? '',
  channelName: communication.channel_name ?? '',
  channelNumber: communication.channel_number ?? '',
  rxFrequency: communication.rx_frequency ?? '',
  rxToneNac: communication.rx_tone_nac ?? '',
  txFrequency: communication.tx_frequency ?? '',
  txToneNac: communication.tx_tone_nac ?? '',
  mode: communication.mode ?? '',
  remarks: communication.remarks ?? '',
});

const toApiPayload = (values: CommunicationFormValues) => {
  const isNonradio = values.type === 'Nonradio';
  return {
    type: values.type,
    function: values.function,
    channel_name: values.channelName,
    channel_number: values.channelNumber || undefined,
    rx_frequency: isNonradio ? '' : values.rxFrequency,
    rx_tone_nac: isNonradio ? '' : values.rxToneNac,
    tx_frequency: isNonradio ? '' : values.txFrequency,
    tx_tone_nac: isNonradio ? '' : values.txToneNac,
    mode: isNonradio ? '' : values.mode,
    remarks: values.remarks,
    assignment: undefined,
  };
};

function TypeSelect({
  value,
  onChange,
  onTypeChange,
}: {
  value: 'Radio' | 'Nonradio';
  onChange: (val: 'Radio' | 'Nonradio') => void;
  onTypeChange?: (val: 'Radio' | 'Nonradio') => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(newValue) => {
        if (!newValue) return;

        onChange(newValue);
        onTypeChange?.(newValue);
      }}
      items={COMS_TYPES}
    >
      <SelectTrigger
        aria-label="Communication type"
        className="tw:min-w-28"
        onClick={(e) => e.stopPropagation()}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {COMS_TYPES.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AddCommunicationRow({
  isPending,
  onSave,
  onCancel,
}: {
  isPending: boolean;
  onSave: (values: CommunicationFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CommunicationFormValues>({
    defaultValues: emptyCommunicationValues,
    resolver: zodResolver(communicationSchema),
  });

  const currentType = useWatch({ control, name: 'type' });
  const isNonradio = currentType === 'Nonradio';

  const validationMessages = Object.values(errors).flatMap((e) =>
    e?.message ? [e.message] : []
  );

  const handleTypeChange = (type: 'Radio' | 'Nonradio') => {
    if (type === 'Nonradio') {
      RADIO_FIELDS.forEach((field) => setValue(field, ''));
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <TypeSelect
                value={field.value}
                onChange={field.onChange}
                onTypeChange={handleTypeChange}
              />
            )}
          />
        </TableCell>
        {COM_COLUMNS.map(({ key, radioOnly }) => (
          <TableCell key={key}>
            {radioOnly && isNonradio ? (
              NULL_VALUE
            ) : (
              <Input
                {...register(key)}
                aria-invalid={Boolean(errors[key])}
                className="tw:min-w-28"
              />
            )}
          </TableCell>
        ))}
        <TableCell>
          <div className="tw:flex tw:gap-2">
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label="Add communication"
              disabled={isPending}
              onClick={() => void handleSubmit(onSave)()}
            >
              <Check />
            </Button>
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label="Cancel adding communication"
              disabled={isPending}
              onClick={onCancel}
            >
              <X />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {validationMessages.length > 0 && (
        <TableRow>
          <TableCell colSpan={11}>
            <div
              role="alert"
              className="tw:grid tw:gap-1 tw:text-xs tw:text-destructive"
            >
              {validationMessages.map((msg) => (
                <p key={msg} className="tw:mb-0!">
                  {msg}
                </p>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function EditableCommunicationRow({
  communication,
  isEditing,
  canEditCards,
  isPending,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDeleteRequest,
}: {
  communication: IncidentComs;
  isEditing: boolean;
  canEditCards: boolean;
  isPending: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (values: CommunicationFormValues) => Promise<void>;
  onDeleteRequest: () => void;
}) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CommunicationFormValues>({
    values: toCommunicationFormValues(communication),
    resolver: zodResolver(communicationSchema),
  });

  const currentType = useWatch({ control, name: 'type' });
  const isNonradio = isEditing
    ? currentType === 'Nonradio'
    : communication.type?.toLowerCase() === 'nonradio';

  const validationMessages = Object.values(errors).flatMap((e) =>
    e?.message ? [e.message] : []
  );

  const handleTypeChange = (type: 'Radio' | 'Nonradio') => {
    if (type === 'Nonradio') {
      RADIO_FIELDS.forEach((field) => setValue(field, ''));
    }
  };

  const getDisplayValue = (key: keyof CommunicationFormValues) => {
    switch (key) {
      case 'channelName':
        return communication.channel_name;
      case 'channelNumber':
        return communication.channel_number;
      case 'rxFrequency':
        return communication.rx_frequency;
      case 'rxToneNac':
        return communication.rx_tone_nac;
      case 'txFrequency':
        return communication.tx_frequency;
      case 'txToneNac':
        return communication.tx_tone_nac;
      case 'function':
        return communication.function ?? communication.name;
      default:
        return communication[key];
    }
  };

  return (
    <>
      <TableRow
        onClick={() => {
          if (canEditCards && !isEditing) onStartEdit();
        }}
        className={cn(canEditCards && !isEditing && 'tw:cursor-pointer')}
      >
        <TableCell>
          {isEditing ? (
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <TypeSelect
                  value={field.value}
                  onChange={field.onChange}
                  onTypeChange={handleTypeChange}
                />
              )}
            />
          ) : (
            communication.type || NULL_VALUE
          )}
        </TableCell>

        {COM_COLUMNS.map(({ key, radioOnly }) => (
          <TableCell key={key}>
            {radioOnly && isNonradio ? (
              NULL_VALUE
            ) : isEditing ? (
              <Input
                {...register(key)}
                aria-invalid={Boolean(errors[key])}
                onClick={(e) => e.stopPropagation()}
                className="tw:min-w-28"
              />
            ) : (
              getDisplayValue(key) || NULL_VALUE
            )}
          </TableCell>
        ))}

        <TableCell>
          {isEditing ? (
            <div className="tw:flex tw:gap-2">
              <Button
                type="button"
                size="icon-sm"
                aria-label="Save communication"
                variant="ghost"
                disabled={isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleSubmit(onSave)();
                }}
              >
                <Check />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Cancel communication changes"
                disabled={isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  reset(toCommunicationFormValues(communication));
                  onCancelEdit();
                }}
              >
                <X />
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="destructive"
              size="icon-sm"
              aria-label="Delete communication"
              disabled={isPending || !canEditCards}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRequest();
              }}
            >
              <Trash />
            </Button>
          )}
        </TableCell>
      </TableRow>

      {isEditing && validationMessages.length > 0 && (
        <TableRow>
          <TableCell colSpan={11}>
            <div
              role="alert"
              className="tw:grid tw:gap-1 tw:text-xs tw:text-destructive"
            >
              {validationMessages.map((msg) => (
                <p key={msg} className="tw:mb-0!">
                  {msg}
                </p>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function CardCommunications({ row, canEditCards }: SectionProps) {
  const [isAddingCommunication, setIsAddingCommunication] = useState(false);
  const [editingCommunicationId, setEditingCommunicationId] = useState<
    string | null
  >(null);
  const [communicationToDelete, setCommunicationToDelete] =
    useState<IncidentComs | null>(null);

  const { data: incidentComs = [] } = useIncidentComs(INCIDENT);
  const groupGuid = useAppSelector(selectGroupGuid);
  const createIncidentComs = useCreateIncidentComs();
  const updateIncidentComs = useUpdateIncidentComs();

  const workGroupComs = useMemo(
    () =>
      incidentComs.filter(
        (com) => com.work_group_id === row.original.workGroupId && !com.archived
      ),
    [incidentComs, row.original.workGroupId]
  );

  const handleAddSubmit = async (values: CommunicationFormValues) => {
    await createIncidentComs.mutateAsync({
      ...toApiPayload(values),
      archived: false,
      group_guid: groupGuid,
      work_group_id: row.original.workGroupId,
    });
    setIsAddingCommunication(false);
  };

  const handleSaveEdit = async (
    comId: string,
    existingCom: IncidentComs,
    values: CommunicationFormValues
  ) => {
    await updateIncidentComs.mutateAsync({
      comsId: comId,
      data: {
        ...existingCom,
        ...toApiPayload(values),
      },
    });
    setEditingCommunicationId(null);
  };

  const handleDelete = async () => {
    if (!communicationToDelete) return;
    await updateIncidentComs.mutateAsync({
      comsId: communicationToDelete.id,
      data: {
        ...communicationToDelete,
        archived: true,
        assignment: undefined,
      },
    });
    setCommunicationToDelete(null);
  };

  return (
    <FocusHighlightCard size="sm">
      <CardHeader>
        <CardTitle>8. Communications</CardTitle>
      </CardHeader>
      <CardContent className="tw:grid tw:gap-y-2">
        <div className="tw:grid tw:gap-2">
          <p className="tw:text-xs">
            These channels are shared with the ICS-205.
          </p>
          <Button
            variant="outline"
            size="sm"
            type="button"
            className="tw:w-fit"
            disabled={!canEditCards || isAddingCommunication}
            onClick={() => {
              setEditingCommunicationId(null);
              setIsAddingCommunication(true);
            }}
          >
            <Plus /> Add Communication
          </Button>

          <div className="tw:mb-2 tw:overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type *</TableHead>
                  {COM_COLUMNS.map((col) => (
                    <TableHead key={col.key}>{col.label}</TableHead>
                  ))}
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isAddingCommunication && (
                  <AddCommunicationRow
                    isPending={createIncidentComs.isPending}
                    onSave={handleAddSubmit}
                    onCancel={() => setIsAddingCommunication(false)}
                  />
                )}

                {workGroupComs.length === 0 && !isAddingCommunication ? (
                  <TableRow>
                    <TableCell colSpan={11}>No communications</TableCell>
                  </TableRow>
                ) : (
                  workGroupComs.map((communication) => (
                    <EditableCommunicationRow
                      key={communication.id}
                      communication={communication}
                      isEditing={editingCommunicationId === communication.id}
                      canEditCards={canEditCards}
                      isPending={updateIncidentComs.isPending}
                      onStartEdit={() => {
                        setIsAddingCommunication(false);
                        setEditingCommunicationId(communication.id);
                      }}
                      onCancelEdit={() => setEditingCommunicationId(null)}
                      onSave={(values) =>
                        handleSaveEdit(communication.id, communication, values)
                      }
                      onDeleteRequest={() =>
                        setCommunicationToDelete(communication)
                      }
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <AlertDialog
          open={communicationToDelete !== null}
          onOpenChange={(open) => {
            if (!open) setCommunicationToDelete(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete communication?</AlertDialogTitle>
              <AlertDialogDescription>
                This communication will be removed from the incident plan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={updateIncidentComs.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={updateIncidentComs.isPending}
                onClick={() => void handleDelete()}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </FocusHighlightCard>
  );
}
